import { mergeSectionOverridesIntoReportData } from './esrSectionOverrides';

export function createDefaultSectionConfig(section) {
  return {
    columnOrder: (section?.columns || []).map((col) => col.key),
    hiddenKeys: [],
    titleOverride: '',
    showTitle: true,
    sortBy: null,
    sortOrder: 'asc'
  };
}

export function createDefaultSectionConfigs(reportData) {
  const configs = {};
  (reportData?.sections || []).forEach((section) => {
    const key = section.instanceId || section.sectionKey;
    configs[key] = createDefaultSectionConfig(section);
  });
  return configs;
}

function parseSortableValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const asNumber = Number(value);
  if (!Number.isNaN(asNumber) && String(value).trim() !== '') {
    return asNumber;
  }
  const asDate = Date.parse(String(value));
  if (!Number.isNaN(asDate) && /^\d{4}-\d{2}-\d{2}/.test(String(value))) {
    return asDate;
  }
  return String(value).toLowerCase();
}

export function sortRows(rows, sortBy, sortOrder) {
  if (!sortBy || !Array.isArray(rows)) return rows || [];
  const direction = sortOrder === 'desc' ? -1 : 1;
  return [...rows].sort((left, right) => {
    const aVal = parseSortableValue(left?.[sortBy]);
    const bVal = parseSortableValue(right?.[sortBy]);
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;
    if (aVal < bVal) return -1 * direction;
    if (aVal > bVal) return 1 * direction;
    return 0;
  });
}

export function getVisibleColumns(section, config) {
  const columnMap = Object.fromEntries((section?.columns || []).map((col) => [col.key, col]));
  return (config?.columnOrder || [])
    .filter((key) => !config?.hiddenKeys?.includes(key))
    .map((key) => columnMap[key])
    .filter(Boolean);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPreviewDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function buildStaticSectionHtml(section) {
  const title = section.title || section.label || section.sectionKey;
  const titleHtml = title ? `<h2 class="section-title">${escapeHtml(title)}</h2>` : '';

  if (section.staticKind === 'prepared-by') {
    const lines = [
      section.preparedByName && `<div>Prepared by: ${escapeHtml(section.preparedByName)}</div>`,
      section.reportDate && `<div>Report date: ${escapeHtml(formatPreviewDate(section.reportDate))}</div>`,
      section.reportName && `<div>Report name: ${escapeHtml(section.reportName)}</div>`,
      section.scopedAircraft && `<div>Aircraft: ${escapeHtml(section.scopedAircraft)}</div>`
    ].filter(Boolean);
    return `<div class="report-section">${titleHtml}${lines.join('') || '<p class="empty-table">No report metadata available.</p>'}</div>`;
  }

  if (section.staticKind === 'aircraft-data') {
    const aircraftList = section.aircraftData || [];
    if (!aircraftList.length) {
      return `<div class="report-section">${titleHtml}<p class="empty-table">No aircraft selected for this report.</p></div>`;
    }
    const items = aircraftList.map((aircraft) => {
      const manufacture = formatPreviewDate(aircraft.dateOfManufacture)
        || aircraft.yearOfManufacture
        || '—';
      const usageAsOn = formatPreviewDate(aircraft.liveUsageAsOnDate)
        ? `${formatPreviewDate(aircraft.liveUsageAsOnDate)}${aircraft.liveUsageSheetNumber ? ` (Sheet ${aircraft.liveUsageSheetNumber})` : ''}`
        : '—';
      const flightHours = aircraft.flightHours != null && aircraft.flightHours !== ''
        ? `${aircraft.flightHours} ${aircraft.hourType || 'Hours'}`.trim()
        : '—';
      const rows = [
        ['Registration', aircraft.registration || '—'],
        ['Manufacturer & Model', aircraft.manufacturerModel || '—'],
        ['Aircraft Type', aircraft.aircraftType || '—'],
        ['Serial Number', aircraft.serialNumber || '—'],
        ['Category', aircraft.category || '—'],
        ['Date of Manufacture', manufacture],
        ['Flight Hours (Live)', flightHours],
        ['Landings (Live)', aircraft.hasLandings ? aircraft.landings : (aircraft.landings || '—')],
        ['Usage As On', usageAsOn],
        ['Basic Empty Weight', aircraft.basicEmptyWeight || '—'],
        ['Max All-Up Weight', aircraft.maxAllUpWeight || '—'],
        ['Max Ramp Weight', aircraft.maxRampWeight || '—'],
        ['Max Landing Weight', aircraft.maxLandingWeight || '—'],
        ['Max Fuel Weight', aircraft.maxFuelWeight || '—'],
        ['Fuel Capacity', aircraft.fuelCapacity || '—'],
        ['Fuel Consumption per Hour', aircraft.fuelConsumptionPerHour || '—'],
        ['Fuel Consumption per Hour (Weight)', aircraft.fuelConsumptionPerHourWeight || '—'],
        ['TSN As On Date', formatPreviewDate(aircraft.tsnAsOnDate) || '—'],
        ['TSN Period Value', aircraft.tsnPeriodValue
          ? `${aircraft.tsnPeriodValue} ${aircraft.tsnPeriod || ''}`.trim()
          : '—'],
        ['TSN Landings', aircraft.tsnLandings || '—']
      ];
      const grid = rows.map(([label, value]) => (
        `<div class="info-item"><div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(value)}</div></div>`
      )).join('');
      return `<div class="static-block aircraft-data-block">
        <h3 class="subsection-title">${escapeHtml(aircraft.registration || 'Aircraft')}</h3>
        <div class="info-grid">${grid}</div>
      </div>`;
    }).join('');
    return `<div class="report-section">${titleHtml}${items}</div>`;
  }

  if (section.staticKind === 'notes-section') {
    return `<div class="report-section">${titleHtml}<p class="notes-block">${escapeHtml(section.notes || '—')}</p></div>`;
  }

  if (section.staticKind === 'esr-cover'
    || section.staticKind === 'esr-glossary'
    || section.staticKind === 'esr-part-divider') {
    const lines = [
      section.periodLabel && `<div><strong>Period:</strong> ${escapeHtml(section.periodLabel)}</div>`,
      section.referenceLine && `<div>${escapeHtml(section.referenceLine)}</div>`,
      section.operatorName && `<div><strong>Operator:</strong> ${escapeHtml(section.operatorName)}</div>`,
      section.operatorAddress && `<div><strong>Address:</strong> ${escapeHtml(section.operatorAddress)}</div>`,
      section.body && `<p class="notes-block">${escapeHtml(section.body)}</p>`
    ].filter(Boolean);
    return `<div class="report-section">${titleHtml}${lines.join('') || '<p class="empty-table">—</p>'}</div>`;
  }

  if (section.staticKind === 'esr-distribution') {
    if (section.body) {
      return `<div class="report-section">${titleHtml}<p class="notes-block">${escapeHtml(section.body)}</p></div>`;
    }
    const lines = section.lines || [];
    const bodyRows = lines.map((row) => (
      `<tr><td>${escapeHtml(row.copyNo)}</td><td>${escapeHtml(row.holder)}</td><td>${escapeHtml(row.distribution)}</td></tr>`
    )).join('');
    const tableHtml = lines.length
      ? `<table class="data-table"><thead><tr><th>Copy No</th><th>Document Holder</th><th>Distribution</th></tr></thead><tbody>${bodyRows}</tbody></table>`
      : '<p class="empty-table">No distribution entries.</p>';
    return `<div class="report-section">${titleHtml}${tableHtml}</div>`;
  }

  if (section.staticKind === 'flight-summary' || section.staticKind === 'crew-block') {
    const columns = section.columns || [];
    const rows = section.rows || [];
    const headerCells = columns.map((col) => `<th>${escapeHtml(col.label)}</th>`).join('');
    const bodyRows = rows.map((row) => (
      `<tr>${columns.map((col) => `<td>${escapeHtml(row[col.key])}</td>`).join('')}</tr>`
    )).join('');
    const tableHtml = rows.length
      ? `<table class="data-table"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
         <p class="table-meta">${section.rowCount ?? rows.length} row(s)${section.truncated ? ' — showing first records' : ''}</p>`
      : '<p class="empty-table">No records available.</p>';
    return `<div class="report-section">${titleHtml}${tableHtml}</div>`;
  }

  return `<div class="report-section">${titleHtml}<p class="table-meta">${escapeHtml(section.label || section.sectionKey)}</p></div>`;
}

function buildTableSectionHtml(section, sectionConfigs) {
  const configKey = section.instanceId || section.sectionKey;
  const config = sectionConfigs[configKey] || createDefaultSectionConfig(section);
  const columns = getVisibleColumns(section, config);
  const rows = sortRows(section.rows, config.sortBy, config.sortOrder);
  const sectionTitle = config.titleOverride || section.title;

  const chartHtml = section.chartSvg
    ? `<div class="esr-chart-block" style="margin:12px 0 16px;text-align:center;">
        ${section.chartTitle ? `<div class="table-meta" style="margin-bottom:8px;font-weight:600;">${escapeHtml(section.chartTitle)}</div>` : ''}
        <div class="esr-chart-svg">${section.chartSvg}</div>
        ${section.chartNote ? `<p class="table-meta" style="margin-top:8px;">${escapeHtml(section.chartNote)}</p>` : ''}
      </div>`
    : '';

  const headerCells = columns.map((col) => `<th>${escapeHtml(col.label)}</th>`).join('');
  const bodyRows = rows.map((row) => (
    `<tr>${columns.map((col) => `<td>${escapeHtml(row[col.key])}</td>`).join('')}</tr>`
  )).join('');

  const showTable = rows.length && !section.hideDataTable;
  const tableHtml = showTable
    ? `<table class="data-table"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
       <p class="table-meta">${section.rowCount} row(s)${section.truncated ? ' — showing first records' : ''}</p>`
    : (rows.length
      ? `<p class="table-meta">${rows.length} aircraft data point(s) plotted on the chart above</p>`
      : (chartHtml ? '' : '<p class="empty-table">No records match the selected filters.</p>'));

  return `<div class="report-section">
    ${config.showTitle === false || !sectionTitle ? '' : `<h2 class="section-title">${escapeHtml(sectionTitle)}</h2>`}
    ${chartHtml}
    ${tableHtml}
  </div>`;
}

export function buildPreviewHtmlFromReportData(reportData, sectionConfigs = {}, options = {}) {
  if (!reportData) return '';
  const showHeading = options.showHeading !== false;
  const heading = options.heading ?? reportData.reportName ?? 'Report';
  const effectiveData = options.sectionOverrides
    ? mergeSectionOverridesIntoReportData(reportData, options.sectionOverrides)
    : reportData;

  const sectionsHtml = (effectiveData.sections || []).map((section) => (
    section.sectionType === 'static'
      ? buildStaticSectionHtml(section)
      : buildTableSectionHtml(section, sectionConfigs)
  )).join('\n');

  const reportDate = reportData.reportDate
    ? new Date(reportData.reportDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=1123, initial-scale=1">
  <title>${escapeHtml(heading || 'Report')}</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11pt; color: #1a1a1a; margin: 0; padding: 3cm 3cm 5cm 3cm; min-width: 297mm; box-sizing: border-box; }
    h1 { font-size: 18pt; margin: 0 0 8px; }
    .meta { font-size: 10pt; color: #555; margin-bottom: 20px; }
    .section-title { font-size: 13pt; font-weight: 600; color: #1565c0; margin: 0 0 12px; text-transform: uppercase; }
    .report-section { margin-bottom: 24px; }
    .data-table { width: max-content; min-width: 100%; border-collapse: collapse; font-size: 8pt; }
    .data-table th, .data-table td { border: 1px solid #ccc; padding: 4px 5px; text-align: left; vertical-align: top; }
    .data-table th { background: #f5f5f5; white-space: nowrap; }
    .table-meta, .empty-table { font-size: 9pt; color: #666; }
    .static-block { margin-bottom: 12px; }
    .notes-block { white-space: pre-wrap; margin: 0; }
    .info-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 20px; }
    .info-item .label { font-size: 8pt; color: #666; text-transform: uppercase; letter-spacing: 0.03em; }
    .info-item .value { font-size: 10pt; font-weight: 500; }
    .subsection-title { font-size: 11pt; font-weight: 600; color: #333; margin: 12px 0 8px; }
  </style>
</head>
<body>
  ${showHeading ? `<h1>${escapeHtml(heading || 'Report')}</h1>` : ''}
  <div class="meta">
    ${reportData.operatorName ? `<div>Operator: ${escapeHtml(reportData.operatorName)}</div>` : ''}
    ${reportData.preparedByName ? `<div>Prepared by: ${escapeHtml(reportData.preparedByName)}</div>` : ''}
    ${reportDate ? `<div>Report date: ${escapeHtml(reportDate)}</div>` : ''}
    ${reportData.scopedAircraft ? `<div>Aircraft: ${escapeHtml(reportData.scopedAircraft)}</div>` : ''}
  </div>
  ${sectionsHtml}
</body>
</html>`;
}
